<template>
	<panel :show="show" class="alert-section" large @close="close">
		<template #title>
			Alert Settings
		</template>
		<template>
			<form>
				<img
					src="@/assets/img/undraw-alert.svg"
					class="panel-illustration"
				/>

				<div class="description">
					Set your audio and visual alerts for new messages, and
					choose the number of minutes of screen inactivity before
					notifications are triggered. If filters are applied, only
					messages that appear in your inbox will trigger alerts.
				</div>

				<!-- Alert Options -->
				<div class="mb-4 switch-container">
					<div
						v-for="{ id, type, is_selected } in alerts"
						:key="id"
						class="switch-row"
					>
						<!-- Toggle Switch -->
						<div class="">
							<label class="switch">
								<input
									type="checkbox"
									:checked="is_selected"
									@change="
										updateAlertStatus(
											id,
											$event.target.checked
										)
									"
								/>
								<span
									class="slider"
									:class="{
										close: !is_selected,
									}"
								>
									{{ is_selected ? 'Yes' : 'No' }}
								</span>
							</label>
						</div>

						<!-- Alert Type Label -->
						<div>
							<label :for="id">
								{{ type }}
							</label>
						</div>
					</div>
				</div>

				<!-- Audio Sound Selection Dropdown -->
				<div
					v-if="isAudibleAlertSelected"
					class="dropdown-control form-group"
				>
					<div
						ref="soundDropdownContainer"
						class="dropdown-container"
					>
						<label class="dropdown-label">
							Audible Alert Sound
						</label>
						<div
							class="custom-form-input  flex items-center justify-between cursor-pointer"
							:class="{
								'opacity-50 pointer-events-none': !isAudibleAlertSelected,
							}"
							tabindex="0"
							@click="
								isAudibleAlertSelected && toggleSoundDropdown()
							"
							@blur="handleSoundDropdownBlur"
							@focusout="handleSoundDropdownBlur"
						>
							<span>{{
								selectedAlertSound
									? selectedAlertSound.name
									: 'Select Sound'
							}}</span>
							<AccordionIconClose v-if="soundIcondown" />
							<AccordionIconOpen v-else />
						</div>
						<div
							v-if="soundIcondown && isAudibleAlertSelected"
							class="custom-dropdown-list"
						>
							<div
								v-for="sound in alertSounds"
								:key="sound.id"
								class="dropdown-item flex force-justify-between"
							>
								<div
									class="flex-1 cursor-pointer"
									@mousedown.prevent.stop="
										setAlertSound(sound)
									"
								>
									<span>{{ sound.name }}</span>
									<audio
										:ref="`audio_${sound.name}`"
										:src="sound.url"
										preload="auto"
										@ended="onAudioEnded"
									></audio>
								</div>

								<div
									@mousedown.prevent.stop
									@click.prevent.stop
								>
									<button
										v-if="currentPlayingId !== sound.name"
										type="button"
										tabindex="-1"
										data-tooltip="Play"
										data-position="left"
										@mousedown.prevent.stop="
											playSound(sound.name)
										"
									>
										<PlayIcon />
									</button>

									<button
										v-else
										type="button"
										tabindex="-1"
										data-tooltip="Pause"
										data-position="left"
										@mousedown.prevent.stop="
											pauseSound(sound.name)
										"
									>
										<PauseIcon />
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Inactivity Timing -->
				<div
					v-if="isAudibleAlertSelected || isVisualAlertSelected"
					class="dropdown-control form-group"
				>
					<div
						ref="timerDropdownContainer"
						class="dropdown-container"
					>
						<label class="dropdown-label">
							Trigger Alerts After Inactivity
						</label>
						<div
							class="custom-form-input  flex items-center justify-between cursor-pointer"
							tabindex="0"
							@click="toggleTimerDropdown"
							@blur="handleTimerDropdownBlur"
						>
							<span>{{
								selectedTimer
									? `${selectedTimer} Minutes`
									: 'Select Interval'
							}}</span>
							<AccordionIconClose v-if="icondown" />
							<AccordionIconOpen v-else />
						</div>
						<div v-if="icondown" class="custom-dropdown-list">
							<div
								v-for="t in timer"
								:key="t"
								class="dropdown-item cursor-pointer"
								@mousedown.prevent.stop="setTimer(t)"
							>
								{{ `${t} Minutes` }}
							</div>
						</div>
					</div>
				</div>

				<!-- Save Button -->
				<div class="btn-wrapper">
					<button
						class="custom-action-button"
						:disabled="disableSaveButton || isSaving"
						@click.prevent="saveAlerts"
					>
						<span>{{ isSaving ? 'Saving...' : 'Save' }}</span>
					</button>
				</div>
			</form>
		</template>
		<template>
			<PanelPopup
				:show="popupBox"
				:disable-save="disableSaveButton"
				@save="saveAlerts"
				@cancel="onCancelClick"
				@exit="onExitClick"
			/>
		</template>
	</panel>
</template>

<script>
import Panel from './Panel'
import PanelMixin from '@/mixins/PanelMixin'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import AccordionIconOpen from '@/components/Icons/AccordionIconOpen.vue'
import AccordionIconClose from '../Icons/AccordionIconClose.vue'
import PlayIcon from '@/components/Icons/PlayIcon.vue'
import PauseIcon from '@/components/Icons/PauseIcon.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import PanelPopup from './PanelPopup.vue'

const ALERT_TYPES = {
	VISUAL: 'Visual Alert',
	AUDIBLE: 'Audible Alert',
}

const INACTIVITY_TIMER_OPTIONS = [1, 2, 3, 5, 10]
const ALERT_COLOR = 'orange'
const DEFAULT_TIMER = 5

const getDefaultAlerts = () => [
	{ id: 1, type: ALERT_TYPES.VISUAL, is_selected: false },
	{ id: 2, type: ALERT_TYPES.AUDIBLE, is_selected: false },
]

const getDefaultInitialState = () => ({
	isVisualAlertSelected: false,
	isAudibleAlertSelected: false,
	selectedTimer: DEFAULT_TIMER,
	selectedAlertSoundUrl: null,
})

export default {
	components: {
		Panel,
		AccordionIconOpen,
		AccordionIconClose,
		PlayIcon,
		PauseIcon,
		PanelPopup,
	},

	computed: {
		disableSaveButton() {
			if (!this.hasUnsavedChanges()) {
				return true
			}
			// If audible alert is on, both sound and timer must be selected
			if (this.isAudibleAlertSelected) {
				return !this.selectedAlertSound || !this.selectedTimer
			}
			// If audible alert is off, save button is enabled
			return false
		},
		alertSounds() {
			return (this.partner?.alert_sounds || []).map((sound, index) => ({
				...sound,
				name: `Alert ${index + 1}`,
			}))
		},

		...mapGetters({
			partner: 'partners/active',
		}),

		...mapState('settings', {
			alertPreferences: 'alertPreferences',
		}),
	},

	methods: {
		close() {
			if (this.hasUnsavedChanges()) {
				this.showPopup()
			} else {
				this.$emit('close')
			}
		},

		hasUnsavedChanges() {
			const current = this.getCurrentState()
			const initial = this.initialState

			if (this.notToContinueWithAudioAlert) {
				return true
			}

			return (
				current.isVisualAlertSelected !==
					initial.isVisualAlertSelected ||
				current.isAudibleAlertSelected !==
					initial.isAudibleAlertSelected ||
				current.selectedTimer !== initial.selectedTimer ||
				current.selectedAlertSoundUrl !== initial.selectedAlertSoundUrl
			)
		},

		getCurrentState() {
			return {
				isVisualAlertSelected: this.isVisualAlertSelected,
				isAudibleAlertSelected: this.isAudibleAlertSelected,
				selectedTimer: this.selectedTimer,
				selectedAlertSoundUrl: this.selectedAlertSound?.url || null,
			}
		},

		loadInitialState() {
			this.initialState = { ...this.getCurrentState() }
		},

		showPopup() {
			this.popupBox = true
		},

		hidePopup() {
			this.popupBox = false
		},

		onExitClick() {
			this.hidePopup()
			this.$emit('close')
		},

		onCancelClick() {
			this.hidePopup()
		},

		async loadAlertSettings() {
			await this.getAlertSettings()
		},

		applyAlertSettings(preferences) {
			if (!preferences) return

			this.preferencesId = preferences.id
			this.isVisualAlertSelected =
				preferences.visual_alert_enabled || false
			this.isAudibleAlertSelected =
				preferences.audio_alert_enabled || false
			if (this.notToContinueWithAudioAlert) {
				this.isAudibleAlertSelected = false
			}
			this.selectedTimer =
				preferences.inactivity_threshold_minutes || DEFAULT_TIMER
			this.prevSelectedTimer = preferences.inactivity_threshold_minutes
			this.selectAlertSound(preferences.alert_sound)
			this.updateAlertsArray()
		},

		updateAlertsArray() {
			this.alerts = this.alerts.map(alert => {
				if (alert.type === ALERT_TYPES.VISUAL) {
					alert.is_selected = this.isVisualAlertSelected
				} else if (alert.type === ALERT_TYPES.AUDIBLE) {
					alert.is_selected = this.isAudibleAlertSelected
				}
				return alert
			})
		},

		selectAlertSound(alertSoundUrl) {
			// If no URL provided but audible alert is enabled, use first sound as default
			if (
				!alertSoundUrl &&
				this.isAudibleAlertSelected &&
				this.alertSounds?.length
			) {
				this.selectedAlertSound = this.alertSounds[0]
				return
			}

			if (!alertSoundUrl || !this.alertSounds?.length) {
				this.selectedAlertSound = null
				return
			}

			// Try exact match first
			let sound = this.alertSounds.find(s => s.url === alertSoundUrl)

			// Fallback to partial/filename match
			if (!sound) {
				const filename = alertSoundUrl.split('/').pop()
				sound = this.alertSounds.find(s => {
					if (!s.url) return false
					return (
						s.url.includes(alertSoundUrl) ||
						alertSoundUrl.includes(s.url) ||
						s.url.split('/').pop() === filename
					)
				})
			}
			this.prevAlertPreferences = sound
			this.selectedAlertSound = sound || null
		},

		resetForm() {
			this.isAudibleAlertSelected = false
			this.isVisualAlertSelected = false
			this.icondown = false
			this.soundIcondown = false
			this.selectedAlertSound = null
			this.currentPlayingId = null
			this.selectedTimer = DEFAULT_TIMER
			this.alerts = getDefaultAlerts()
			this.isSaving = false
			this.preferencesId = null
			this.initialState = getDefaultInitialState()
		},

		async saveAlerts() {
			this.isSaving = true
			try {
				const userId = this.$store?.state?.auth?.user?.id
				if (!userId) {
					throw new Error('User not found')
				}
				const payload = this.buildSavePayload()
				const response = this.preferencesId
					? await this.updatePreference({
							payload,
							id: this.preferencesId,
					  })
					: await this.setPreference(payload)

				this.handleSaveSuccess(response)
			} catch (error) {
				this.$alert.error(
					error.message || 'Failed to save alert settings.'
				)
			} finally {
				this.isSaving = false
			}
		},

		buildSavePayload() {
			const NoAlertSelected =
				!this.isAudibleAlertSelected && !this.isVisualAlertSelected

			return {
				omd: {
					audio_alert_enabled: this.isAudibleAlertSelected,
					visual_alert_enabled: this.isVisualAlertSelected,
					inactivity_threshold_minutes: NoAlertSelected
						? this.prevSelectedTimer
						: this.selectedTimer,
					alert_sound: !this.isAudibleAlertSelected
						? this.prevAlertPreferences?.url || null
						: this.selectedAlertSound?.url || null,
					alert_color: ALERT_COLOR,
				},
			}
		},

		handleSaveSuccess(response) {
			this.loadInitialState()
			this.hidePopup()
			this.$emit('close')
			this.$alert.success(
				response.data?.message || 'Alert settings saved successfully.'
			)
		},

		updateAlertStatus(id, status) {
			const alert = this.alerts.find(a => a.id === id)
			if (!alert) return

			alert.is_selected = status

			if (alert.type === ALERT_TYPES.VISUAL) {
				this.isVisualAlertSelected = status
			} else if (alert.type === ALERT_TYPES.AUDIBLE) {
				this.isAudibleAlertSelected = status
				if (status) {
					// If audible alert is enabled and no sound is selected,
					// automatically select the first sound from the list
					if (!this.selectedAlertSound) {
						if (this.alertSounds?.length > 0) {
							this.selectedAlertSound = this.alertSounds[0]
						} else {
							this.selectedAlertSound = null
						}
					}
				} else {
					this.closeAllDropdowns()
				}
			}
		},

		toggleTimerDropdown() {
			this.icondown = !this.icondown
		},

		toggleSoundDropdown() {
			this.soundIcondown = !this.soundIcondown
		},

		setTimer(timerValue) {
			this.selectedTimer = timerValue
			this.icondown = false
		},

		setAlertSound(sound) {
			this.selectedAlertSound = sound
			this.soundIcondown = false
		},

		closeAllDropdowns() {
			this.icondown = false
			this.soundIcondown = false
		},

		handleSoundDropdownBlur(event) {
			this.handleDropdownBlur(
				event,
				'soundDropdownContainer',
				'soundIcondown'
			)
		},

		handleTimerDropdownBlur(event) {
			this.handleDropdownBlur(event, 'timerDropdownContainer', 'icondown')
		},

		handleDropdownBlur(event, containerRef, stateProperty) {
			setTimeout(() => {
				const container = this.$refs[containerRef]
				const relatedTarget = event.relatedTarget
				const activeElement = document.activeElement

				// Don't close if clicking inside the dropdown (including buttons)
				if (container && container.contains(activeElement)) {
					return
				}

				// Don't close if the related target (where focus is going) is inside the container
				if (
					relatedTarget &&
					container &&
					container.contains(relatedTarget)
				) {
					return
				}

				// Close dropdown if focus moved outside
				if (
					container &&
					!container.contains(activeElement) &&
					(!relatedTarget || !container.contains(relatedTarget))
				) {
					this[stateProperty] = false
				}
			}, 200)
		},

		handleClickOutside(event) {
			const soundContainer = this.$refs.soundDropdownContainer
			const timerContainer = this.$refs.timerDropdownContainer

			// Check if click is inside sound dropdown
			if (
				soundContainer &&
				this.soundIcondown &&
				!soundContainer.contains(event.target)
			) {
				this.soundIcondown = false
			}

			// Check if click is inside timer dropdown
			if (
				timerContainer &&
				this.icondown &&
				!timerContainer.contains(event.target)
			) {
				this.icondown = false
			}
		},

		getAudioElement(name) {
			const audioEl = this.$refs[`audio_${name}`]
			return Array.isArray(audioEl) ? audioEl[0] : audioEl
		},

		async playSound(name) {
			const audio = this.getAudioElement(name)
			if (!audio) return

			try {
				// Safari requires audio to be loaded and play() promise to be handled
				if (audio.readyState < 2) {
					audio.load()
					// Wait for audio to be ready (Safari requirement)
					await new Promise((resolve, reject) => {
						const timeout = setTimeout(() => {
							reject(new Error('Audio load timeout'))
						}, 5000)

						const onCanPlay = () => {
							clearTimeout(timeout)
							audio.removeEventListener('canplay', onCanPlay)
							audio.removeEventListener('error', onError)
							resolve()
						}

						const onError = () => {
							clearTimeout(timeout)
							audio.removeEventListener('canplay', onCanPlay)
							audio.removeEventListener('error', onError)
							reject(new Error('Audio load error'))
						}

						audio.addEventListener('canplay', onCanPlay)
						audio.addEventListener('error', onError)
					})
				}

				// Reset to beginning and play
				audio.currentTime = 0
				await audio.play()
				this.currentPlayingId = name
			} catch (error) {
				// Handle Safari autoplay restrictions
				console.warn('Failed to play audio:', error)
				this.currentPlayingId = null
			}
		},

		pauseSound(name) {
			const audio = this.getAudioElement(name)
			if (audio) {
				audio.pause()
				this.currentPlayingId = null
			}
		},

		onAudioEnded() {
			this.currentPlayingId = null
		},

		...mapActions('settings', [
			'setPreference',
			'getAlertSettings',
			'updatePreference',
		]),
	},

	mixins: [PanelMixin],

	name: 'AlertPanel',

	props: {
		notToContinueWithAudioAlert: {
			type: Boolean,
			default: false,
		},
	},

	watch: {
		async show(visible) {
			if (visible) {
				try {
					await this.loadAlertSettings()
				} catch (error) {
					this.$alert.error('Failed to load alert settings.')
				}
			} else {
				this.resetForm()
			}
		},

		alertPreferences: {
			handler(newPreferences) {
				if (this.show && newPreferences) {
					this.applyAlertSettings(newPreferences)
					this.loadInitialState()
				}
			},
			deep: true,
		},
	},

	mounted() {
		document.addEventListener('click', this.handleClickOutside)
	},

	beforeDestroy() {
		document.removeEventListener('click', this.handleClickOutside)
	},

	data() {
		return {
			alerts: getDefaultAlerts(),
			isVisualAlertSelected: false,
			isAudibleAlertSelected: false,
			timer: INACTIVITY_TIMER_OPTIONS,
			selectedTimer: 5,
			icondown: false,
			soundIcondown: false,
			selectedAlertSound: null,
			currentPlayingId: null,
			isSaving: false,
			preferencesId: null,
			popupBox: false,
			initialState: getDefaultInitialState(),
			prevAlertPreferences: null,
			prevSelectedTimer: null,
		}
	},
}
</script>
